import "./styles.scss";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Text, Button } from "@fragments";
import {
    IconArrowLeft,
    IconCheck,
    IconX,
    IconBrandWhatsapp,
    IconClipboardCheck,
} from "@tabler/icons-react";

/**
 * Floating review bar shown when the answers page is opened from a mentoring
 * enrollment (EnrollmentDetailView → "Visualizar aplicação"). Borrows the
 * community "create post" floater design: fixed, centered, rounded card that
 * slides up from the bottom.
 *
 * `canReview` (status === "pending") adds the Approve/Reject actions. Those are
 * placeholders until the review flow (PATCH …/enrollments/{id}/review, ClickUp
 * 868jxkz4p stage 4) is wired — they mirror the toast used elsewhere.
 */
export default function MentoringReviewBar({ returnTo, canReview, phone }) {
    const { t } = useTranslation("settings");
    const navigate = useNavigate();

    function goBack() {
        if (returnTo) navigate(returnTo);
    }

    // wa.me expects digits only — strip the leading "+" and any spacing.
    function openWhatsApp() {
        const digits = String(phone ?? "").replace(/\D/g, "");
        if (digits) window.open(`https://wa.me/${digits}`, "_blank", "noopener");
    }

    function reviewPlaceholder() {
        window.setSnackbar?.(t("forms.submissions.details.review.placeholder"));
    }

    return (
        <div
            className="mentoring-review-bar is-visible"
            role="region"
            aria-label={t("forms.submissions.details.review.ariaLabel")}
        >
            <div className="mentoring-review-bar__card">
                <div className="mentoring-review-bar__title">
                    <span className="mentoring-review-bar__title-icon">
                        <IconClipboardCheck size={18} />
                    </span>
                    <div className="mentoring-review-bar__title-text">
                        <Text.span contrast={9} weight={500}>{t("forms.submissions.details.review.title")}</Text.span>
                        <Text.span contrast={5}>
                            {canReview ? t("forms.submissions.details.review.canReview") : t("forms.submissions.details.review.readonly")}
                        </Text.span>
                    </div>
                </div>

                <div className="mentoring-review-bar__actions">
                    <Button
                        size="small"
                        color="overlay"
                        variant="rounded"
                        startIcon={<IconArrowLeft size={16} />}
                        onClick={goBack}
                        tooltip={t("forms.submissions.details.review.backTooltip")}
                        preventLoading
                    >
                        {t("common:actions.back")}
                    </Button>

                    {phone && (
                        <Button
                            size="small"
                            color="overlay"
                            variant="rounded"
                            icon={<IconBrandWhatsapp size={16} />}
                            onClick={openWhatsApp}
                            tooltip={t("forms.submissions.details.review.whatsappTooltip")}
                            preventLoading
                        />
                    )}

                    {canReview && (
                        <>
                            <Button
                                size="small"
                                color="error"
                                startIcon={<IconX size={16} />}
                                onClick={reviewPlaceholder}
                                preventLoading
                                discreet
                            >
                                {t("forms.submissions.details.review.reject")}
                            </Button>
                            <Button
                                size="small"
                                color="success"
                                startIcon={<IconCheck size={16} />}
                                onClick={reviewPlaceholder}
                                preventLoading
                            >
                                {t("forms.submissions.details.review.approve")}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
